import { supabase } from "@/integrations/supabase/client";
import { captureFirstTouchIfAbsent, parseUtmFromSearch } from "./attribution";

const SESSION_KEY = "analytics_session_id";
const SESSION_TS_KEY = "analytics_session_ts";
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min idle

function getSessionId(): string {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(SESSION_TS_KEY) || 0);
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id || now - last > SESSION_TTL_MS) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch {
    return "no-session";
  }
}

interface ActiveView {
  id: string | null;
  path: string;
  startedAt: number;
  durationSent: boolean;
}

let active: ActiveView | null = null;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function flushDuration() {
  if (!active || active.durationSent || !active.id) return;
  const duration = Date.now() - active.startedAt;
  active.durationSent = true;

  // Use a SECURITY DEFINER RPC so the call works for anonymous bouncing
  // visitors without depending on PostgREST PATCH semantics, and so that
  // `navigator.sendBeacon` (POST-only) can deliver the payload reliably during
  // pagehide / beforeunload — the moment the visitor actually leaves.
  const url = `${SUPABASE_URL}/rest/v1/rpc/update_page_view_duration`;
  const body = JSON.stringify({ p_id: active.id, p_duration_ms: duration });

  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // sendBeacon needs the apikey embedded in the URL since it can't set headers.
      const beaconUrl = `${url}?apikey=${SUPABASE_KEY}`;
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(beaconUrl, blob);
      if (ok) return;
    }
    // Fallback: keepalive fetch.
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
      },
      body,
      keepalive: true,
    });
  } catch {
    // swallow
  }
}

export async function trackPageView(path: string) {
  // Flush previous view's duration first
  await flushDuration();

  const session_id = getSessionId();
  const { data: { session } } = await supabase.auth.getSession().catch(() => ({ data: { session: null } } as any));

  // Parse UTMs from current URL and seed first-touch attribution (first write wins).
  const search = typeof window !== "undefined" ? window.location.search : "";
  const pathname = typeof window !== "undefined" ? window.location.pathname : path.split("?")[0];
  const utms = parseUtmFromSearch(search);
  captureFirstTouchIfAbsent(pathname, search);

  // Generate id client-side so we can PATCH duration later without needing a SELECT policy on page_views
  const id = crypto.randomUUID();
  const row = {
    id,
    session_id,
    user_id: session?.user?.id ?? null,
    path,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    utm_source: utms.utm_source,
    utm_medium: utms.utm_medium,
    utm_campaign: utms.utm_campaign,
    utm_term: utms.utm_term,
    utm_content: utms.utm_content,
    landing_path: pathname,
  };

  const { error } = await supabase.from("page_views").insert(row);

  if (error) {
    console.warn("[analytics] insert failed", error.message);
    active = { id: null, path, startedAt: Date.now(), durationSent: true };
    return;
  }
  active = { id, path, startedAt: Date.now(), durationSent: false };
}

let listenersAttached = false;

export function attachAnalyticsListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  const handleHidden = () => {
    if (document.visibilityState === "hidden") flushDuration();
  };
  window.addEventListener("visibilitychange", handleHidden);
  window.addEventListener("pagehide", flushDuration);
  window.addEventListener("beforeunload", flushDuration);
}
